import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { socialPostApi } from '../../api/socialPostApi';
import { resolveSafeUserVisibleError } from '@formosa/shared/i18n/safeUserVisibleError';
import { resolveMemberMediaUrl } from '@formosa/shared/utils/mediaUrl';
import { useAuth } from '../../auth/AuthContext';
import SafeImage from '../../components/SafeImage';
import { travelMemberProductDisplayName } from '../member-identity/memberIdentityPresentation';
import SocialLocationSelector from './SocialLocationSelector';
import SocialJourneyAttachmentPicker from './SocialJourneyAttachmentPicker';
import SocialPostMediaEditor from './SocialPostMediaEditor';
import SocialQuoteReferenceCard from '../social-post/SocialQuoteReferenceCard';
import { hasPendingMediaUpload } from '../social-post/socialPostMediaModel';
import { quotePreviewFromDetail } from '../social-post/socialQuoteReferenceModel';
import {
  SOCIAL_COMPOSE_KINDS,
  SOCIAL_COMPOSE_KIND_LABELS,
  SOCIAL_COMPOSE_VISIBILITIES,
  SOCIAL_COMPOSE_VISIBILITY_LABELS,
  SOCIAL_COMPOSE_REPLY_POLICIES,
  SOCIAL_COMPOSE_REPLY_POLICY_LABELS,
  SOCIAL_COMPOSE_QUOTE_POLICIES,
  SOCIAL_COMPOSE_QUOTE_POLICY_LABELS,
  SOCIAL_COMPOSE_DEFAULT_REPLY_POLICY,
  SOCIAL_COMPOSE_DEFAULT_QUOTE_POLICY,
  SOCIAL_TEXT_MAX,
  buildCreateSocialPostBody,
  createdSocialPostId,
  createSocialComposeClientRequestId,
  snowflakePositiveId,
  validateSocialComposeDraft,
} from './socialComposeModel';
import {
  SOCIAL_ACTION_PRODUCT_CHROME as socialChrome,
  SOCIAL_COMPOSE_PRODUCT_CHROME as composeChrome,
} from '../social-post/socialActionPresentation';
import './social-compose.css';

function composeAudienceSummary(visibility, replyPolicy) {
  const vis = {
    PUBLIC: '公開貼文',
    FOLLOWERS: '追蹤者可見',
    PRIVATE: '僅自己可見',
  }[visibility] || '貼文';
  const reply = {
    EVERYONE: '所有人',
    FOLLOWERS: '追蹤者',
    NONE: '不可回覆',
  }[replyPolicy] || '';
  return reply ? `${vis} · ${reply}` : vis;
}

export default function SocialPostComposePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const headingId = useId();
  const kindId = useId();
  const visibilityId = useId();
  const replyPolicyId = useId();
  const quotePolicyId = useId();
  const textId = useId();
  const pendingId = useId();
  const errorId = useId();
  const optionsTitleId = useId();
  const mediaInputId = useId();
  const submitting = useRef(false);
  const textAreaRef = useRef(null);
  const optionsCloseRef = useRef(null);
  const optionsTriggerRef = useRef(null);
  const moreMenuRef = useRef(null);
  const clientRequestIdRef = useRef(createSocialComposeClientRequestId());
  const [postKind, setPostKind] = useState('MOOD');
  const [visibility, setVisibility] = useState('PUBLIC');
  const [replyPolicy, setReplyPolicy] = useState(SOCIAL_COMPOSE_DEFAULT_REPLY_POLICY);
  const [quotePolicy, setQuotePolicy] = useState(SOCIAL_COMPOSE_DEFAULT_QUOTE_POLICY);
  const [textContent, setTextContent] = useState('');
  const [mediaItems, setMediaItems] = useState([]);
  const [locationNode, setLocationNode] = useState(null);
  const [linkedJourneyPublicationId, setLinkedJourneyPublicationId] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [formError, setFormError] = useState('');
  const [busy, setBusy] = useState(false);
  const [optionsOpen, setOptionsOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [quotedSocialPostId, setQuotedSocialPostId] = useState(
    () => snowflakePositiveId(location.state?.quotedSocialPostId),
  );
  const [quotePreview, setQuotePreview] = useState(null);
  const [quotePreviewPhase, setQuotePreviewPhase] = useState(
    () => (snowflakePositiveId(location.state?.quotedSocialPostId) ? 'loading' : 'idle'),
  );

  const displayName = travelMemberProductDisplayName(user, { genericFallback: '旅人' });
  const avatarUrl = resolveMemberMediaUrl(user?.avatarUrl || user?.avatar);
  const audienceSummary = composeAudienceSummary(visibility, replyPolicy);

  const growTextarea = useCallback(() => {
    const node = textAreaRef.current;
    if (!node) return;
    node.style.height = 'auto';
    const maxHeight = 280;
    const nextHeight = Math.min(node.scrollHeight, maxHeight);
    node.style.height = `${Math.max(nextHeight, 120)}px`;
    node.style.overflowY = node.scrollHeight > maxHeight ? 'auto' : 'hidden';
  }, []);

  useEffect(() => {
    growTextarea();
  }, [textContent, growTextarea]);

  useEffect(() => {
    if (!quotedSocialPostId) {
      setQuotePreview(null);
      setQuotePreviewPhase('idle');
      return undefined;
    }
    let cancelled = false;
    setQuotePreviewPhase('loading');
    setQuotePreview(null);
    (async () => {
      try {
        const detail = await socialPostApi.getDetail(quotedSocialPostId);
        if (cancelled) return;
        setQuotePreview(quotePreviewFromDetail(detail));
        setQuotePreviewPhase('ready');
      } catch {
        if (cancelled) return;
        setQuotePreview({
          postId: quotedSocialPostId,
          available: false,
          authorName: '',
          text: '',
          coverUrl: '',
        });
        setQuotePreviewPhase('unavailable');
      }
    })();
    return () => { cancelled = true; };
  }, [quotedSocialPostId]);

  useEffect(() => {
    if (!optionsOpen) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const trigger = optionsTriggerRef.current;
    const onKey = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        setOptionsOpen(false);
      }
    };
    document.addEventListener('keydown', onKey);
    optionsCloseRef.current?.focus();
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', onKey);
      trigger?.focus();
    };
  }, [optionsOpen]);

  useEffect(() => {
    if (!moreOpen) return undefined;
    const onKey = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        setMoreOpen(false);
      }
    };
    const onPointer = (event) => {
      if (moreMenuRef.current && !moreMenuRef.current.contains(event.target)) {
        setMoreOpen(false);
      }
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onPointer);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onPointer);
    };
  }, [moreOpen]);

  function removeQuoteReference() {
    setQuotedSocialPostId(null);
    setQuotePreview(null);
    setQuotePreviewPhase('idle');
  }

  async function onSubmit(event) {
    event.preventDefault();
    if (submitting.current || hasPendingMediaUpload(mediaItems)) return;
    const draft = {
      postKind,
      visibility,
      replyPolicy,
      quotePolicy,
      textContent,
      mediaItems,
      linkedLocationId: postKind === 'FOOD' ? locationNode?.locationId : null,
      linkedJourneyPublicationId,
      quotedSocialPostId,
    };
    const nextErrors = validateSocialComposeDraft(draft);
    setFieldErrors(nextErrors);
    setFormError('');
    if (Object.keys(nextErrors).length) {
      setOptionsOpen(true);
      return;
    }

    submitting.current = true;
    setBusy(true);
    try {
      const created = await socialPostApi.create(buildCreateSocialPostBody(draft, {
        clientRequestId: clientRequestIdRef.current,
      }));
      const postId = createdSocialPostId(created);
      if (!postId) throw new Error('Post was created without an id.');
      navigate(`/social/posts/${postId}`, { replace: true });
    } catch (cause) {
      const key = String(cause?.raw?.key || cause?.code || '');
      if (key.includes('travel.social.post.conflict') || cause?.raw?.code === 95703) {
        clientRequestIdRef.current = createSocialComposeClientRequestId();
      }
      if (key.includes('travel.social.post.validation.failed') || cause?.raw?.code === 95700) {
        setFormError('貼文內容無效，請檢查後再試。');
      } else if (key.includes('travel.social.safety') || cause?.status === 403) {
        setFormError(resolveSafeUserVisibleError(
          cause,
          () => '無法引用此來源，請稍後再試。',
          'travel.common.actionFailed',
        ));
      } else {
        setFormError(resolveSafeUserVisibleError(
          cause,
          () => composeChrome.publishFailed,
          'travel.common.actionFailed',
        ));
      }
      submitting.current = false;
      setBusy(false);
    }
  }

  return (
    <main className="social-compose social-compose--viewport" aria-labelledby={headingId}>
      <header className="social-compose__topbar">
        <button
          type="button"
          className="social-compose__close"
          aria-label={composeChrome.closeAria}
          data-testid="social-compose-close"
          onClick={() => navigate('/explore')}
        >
          ×
        </button>
        <h1 id={headingId} className="social-compose__title">{composeChrome.title}</h1>
        <div className="social-compose__topbar-actions" ref={moreMenuRef}>
          <button
            type="button"
            className="social-compose__more"
            aria-label={composeChrome.moreMenuAria}
            aria-expanded={moreOpen}
            aria-haspopup="menu"
            data-testid="social-compose-more"
            onClick={() => setMoreOpen((open) => !open)}
          >
            •••
          </button>
          {moreOpen ? (
            <div className="social-compose__more-menu" role="menu" data-testid="social-compose-more-menu">
              <button type="button" role="menuitem" disabled title={composeChrome.gapUnavailable}>
                <span>{composeChrome.aiTagGap}</span>
                <span aria-hidden="true">✦</span>
              </button>
              <button type="button" role="menuitem" disabled title={composeChrome.gapUnavailable}>
                <span>{composeChrome.scheduleGap}</span>
                <span aria-hidden="true">◷</span>
              </button>
              <p className="social-compose__gap-note">{composeChrome.gapUnavailable}</p>
            </div>
          ) : null}
        </div>
      </header>

      <form className="social-compose__form social-compose__form--stack" onSubmit={onSubmit} noValidate>
        <div className="social-compose__scroll" data-testid="social-compose-scroll">
        {quotedSocialPostId ? (
          <section className="social-compose__quote" aria-live="polite">
            {quotePreviewPhase === 'loading' ? (
              <p>{socialChrome.quoteLoading}</p>
            ) : (
              <SocialQuoteReferenceCard quote={quotePreview} />
            )}
            <button
              type="button"
              className="social-compose__remove-quote"
              aria-label={socialChrome.removeQuoteAria}
              disabled={busy}
              onClick={removeQuoteReference}
            >
              {socialChrome.removeQuote}
            </button>
          </section>
        ) : null}

        <div className="social-compose__author">
          <div className="social-compose__avatar" aria-hidden="true">
            {avatarUrl ? (
              <SafeImage src={avatarUrl} alt="" className="social-compose__avatar-img" />
            ) : (
              <span className="social-compose__avatar-fallback">{displayName.slice(0, 1)}</span>
            )}
          </div>
          <div className="social-compose__author-meta">
            <p className="social-compose__author-name">{displayName}</p>
            <p className="social-compose__audience" data-testid="social-compose-audience">{audienceSummary}</p>
          </div>
        </div>

        <div className="social-compose__field social-compose__field--text">
          <label className="visually-hidden" htmlFor={textId}>內容</label>
          <textarea
            ref={textAreaRef}
            id={textId}
            className="social-compose__textarea-grow"
            value={textContent}
            onChange={(event) => setTextContent(event.target.value)}
            maxLength={SOCIAL_TEXT_MAX}
            disabled={busy}
            placeholder={composeChrome.textPlaceholder}
            aria-describedby={`${textId}-count${fieldErrors.textContent ? ` ${textId}-error` : ''}`}
            aria-invalid={Boolean(fieldErrors.textContent)}
          />
          <span id={`${textId}-count`} className="social-compose__count">{textContent.length} / {SOCIAL_TEXT_MAX}</span>
          {fieldErrors.textContent ? <span id={`${textId}-error`} role="alert">{fieldErrors.textContent}</span> : null}
        </div>

        <section className="social-compose__media" aria-label={composeChrome.mediaLegend}>
          <SocialPostMediaEditor
            items={mediaItems}
            inputId={mediaInputId}
            hideAddTrigger={false}
            disabled={busy}
            onChange={(next) => setMediaItems((current) => (typeof next === 'function' ? next(current) : next))}
            onError={(cause) => setFormError(resolveSafeUserVisibleError(
              cause,
              () => cause?.message || composeChrome.coverUploadFailed,
              'travel.common.actionFailed',
            ))}
          />
          {fieldErrors.media ? <span role="alert">{fieldErrors.media}</span> : null}
        </section>

        </div>

        <div className="social-compose__tools social-compose__tools--sticky" role="toolbar" aria-label="發文工具">
          <label className="social-compose__tool is-active" htmlFor={mediaInputId}>
            <span className="social-compose__tool-icon" aria-hidden="true">▧</span>
            <span>{composeChrome.addImageShort}</span>
          </label>
          <button type="button" className="social-compose__tool" disabled title={composeChrome.gapUnavailable}>
            <span className="social-compose__tool-icon" aria-hidden="true">GIF</span>
            <span>{composeChrome.toolGif}</span>
          </button>
          <button type="button" className="social-compose__tool" disabled title={composeChrome.gapUnavailable}>
            <span className="social-compose__tool-icon" aria-hidden="true">☺</span>
            <span>{composeChrome.toolSticker}</span>
          </button>
          <button type="button" className="social-compose__tool" disabled title={composeChrome.gapUnavailable}>
            <span className="social-compose__tool-icon" aria-hidden="true">♫</span>
            <span>{composeChrome.toolMusic}</span>
          </button>
          <button
            type="button"
            className="social-compose__tool"
            disabled={busy}
            onClick={() => setOptionsOpen(true)}
          >
            <span className="social-compose__tool-icon" aria-hidden="true">•••</span>
            <span>{composeChrome.toolMore}</span>
          </button>
        </div>

        {busy ? <p id={pendingId} aria-live="polite">{composeChrome.publishing}</p> : null}
        {formError ? <p id={errorId} className="social-compose__error" role="alert" aria-live="assertive">{formError}</p> : null}

        <div className="social-compose__footer social-compose__footer--sticky">
          <button
            ref={optionsTriggerRef}
            type="button"
            className="social-compose__options-entry"
            data-testid="social-compose-options"
            disabled={busy}
            onClick={() => setOptionsOpen(true)}
          >
            <span aria-hidden="true">⚙</span>
            {composeChrome.postOptions}
          </button>
          <button
            type="submit"
            className="social-compose__publish"
            disabled={busy || hasPendingMediaUpload(mediaItems)}
            aria-busy={busy || hasPendingMediaUpload(mediaItems)}
          >
            {busy ? composeChrome.publishing : composeChrome.publish}
          </button>
        </div>

        {optionsOpen ? (
          <div
            className="social-compose__options-scrim"
            data-testid="social-compose-options-scrim"
            role="presentation"
            onClick={() => setOptionsOpen(false)}
          >
            <div
              className="social-compose__options-sheet"
              role="dialog"
              aria-modal="true"
              aria-labelledby={optionsTitleId}
              data-testid="social-compose-options-sheet"
              onClick={(event) => event.stopPropagation()}
            >
              <div className="social-compose__options-head">
                <h2 id={optionsTitleId}>{composeChrome.postOptionsTitle}</h2>
                <button
                  ref={optionsCloseRef}
                  type="button"
                  className="social-compose__options-done"
                  onClick={() => setOptionsOpen(false)}
                >
                  {composeChrome.postOptionsDone}
                </button>
              </div>

              <fieldset className="social-compose__field" disabled={busy}>
                <legend id={kindId}>類型</legend>
                <div className="social-compose__choices" role="radiogroup" aria-labelledby={kindId}>
                  {SOCIAL_COMPOSE_KINDS.map((kind) => (
                    <label key={kind}>
                      <input
                        type="radio"
                        name="postKind"
                        value={kind}
                        checked={postKind === kind}
                        onChange={() => setPostKind(kind)}
                      />
                      {SOCIAL_COMPOSE_KIND_LABELS[kind]}
                    </label>
                  ))}
                </div>
                {fieldErrors.postKind ? <span role="alert">{fieldErrors.postKind}</span> : null}
              </fieldset>

              <fieldset className="social-compose__field" disabled={busy}>
                <legend id={visibilityId}>可見範圍</legend>
                <div className="social-compose__choices" role="radiogroup" aria-labelledby={visibilityId}>
                  {SOCIAL_COMPOSE_VISIBILITIES.map((value) => (
                    <label key={value}>
                      <input
                        type="radio"
                        name="visibility"
                        value={value}
                        checked={visibility === value}
                        onChange={() => setVisibility(value)}
                      />
                      {SOCIAL_COMPOSE_VISIBILITY_LABELS[value]}
                    </label>
                  ))}
                </div>
                {fieldErrors.visibility ? <span role="alert">{fieldErrors.visibility}</span> : null}
              </fieldset>

              <fieldset className="social-compose__field" disabled={busy}>
                <legend id={replyPolicyId}>誰可以回覆</legend>
                <div className="social-compose__choices" role="radiogroup" aria-labelledby={replyPolicyId}>
                  {SOCIAL_COMPOSE_REPLY_POLICIES.map((value) => (
                    <label key={value}>
                      <input
                        type="radio"
                        name="replyPolicy"
                        value={value}
                        checked={replyPolicy === value}
                        onChange={() => setReplyPolicy(value)}
                      />
                      {SOCIAL_COMPOSE_REPLY_POLICY_LABELS[value]}
                    </label>
                  ))}
                </div>
                {fieldErrors.replyPolicy ? <span role="alert">{fieldErrors.replyPolicy}</span> : null}
              </fieldset>

              <fieldset className="social-compose__field" disabled={busy}>
                <legend id={quotePolicyId}>引用</legend>
                <div className="social-compose__choices" role="radiogroup" aria-labelledby={quotePolicyId}>
                  {SOCIAL_COMPOSE_QUOTE_POLICIES.map((value) => (
                    <label key={value}>
                      <input
                        type="radio"
                        name="quotePolicy"
                        value={value}
                        checked={quotePolicy === value}
                        onChange={() => setQuotePolicy(value)}
                      />
                      {SOCIAL_COMPOSE_QUOTE_POLICY_LABELS[value]}
                    </label>
                  ))}
                </div>
                {fieldErrors.quotePolicy ? <span role="alert">{fieldErrors.quotePolicy}</span> : null}
              </fieldset>

              {postKind === 'FOOD' ? (
                <fieldset className="social-compose__field" disabled={busy}>
                  <legend>{composeChrome.locationLegend}</legend>
                  <p className="social-compose__hint">{composeChrome.locationHint}</p>
                  <SocialLocationSelector
                    selected={locationNode}
                    onSelect={setLocationNode}
                    disabled={busy}
                    searchable
                  />
                </fieldset>
              ) : null}

              <SocialJourneyAttachmentPicker
                value={linkedJourneyPublicationId}
                onChange={setLinkedJourneyPublicationId}
                disabled={busy}
              />

              <p className="social-compose__helper">{composeChrome.helper}</p>
              <Link className="social-compose__back-link" to="/explore">{composeChrome.backToExplore}</Link>
            </div>
          </div>
        ) : null}
      </form>
    </main>
  );
}
